/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import React, { useContext } from 'react';
import { ItemLinkProps } from '../../../interfaces';
import { TrackingServiceContext, useInPageNavContext } from '../../context';
import { StyledLink } from './NavigationStyles';
import { getIndexPosition, useTracking } from '../../hooks/use-tracking';
import {
  subLinksRelatedSingleElement,
  topLevelRelatedSingleElement,
  trackClickEvent,
} from '../../utils/tracking-utils';
import { hasSubLinks } from '../../utils/utils';

export const ItemLink = ({
  id,
  index,
  path,
  handleSelectedItem,
  hasSubMenu,
  subLevelChildLabel,
  title,
  ariaLabel,
  isLastElement,
  hasParentElement,
  isSecondaryLevel,
  prioprityNavigationActiveIndex,
  target,
}: ItemLinkProps) => {
  const {
    parentPageInpageNavigation,
    sameLevelInpageNavigation,
    sameLevelActiveLink,
    topLevelActiveLink,
    secondLevelActiveLink,
    subNavActive,
    impressionTracking,
  } = useInPageNavContext();
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);

  const currentPath = typeof window !== 'undefined' ? window?.location?.pathname : '';
  let secondLevelActive =
    secondLevelActiveLink?.href === currentPath &&
    secondLevelActiveLink?.urlIndex === index &&
    !subNavActive;
  // second level active links
  if (subNavActive) {
    secondLevelActive =
      (secondLevelActiveLink?.href === currentPath &&
        secondLevelActiveLink?.urlIndex === index &&
        topLevelActiveLink?.index === secondLevelActiveLink?.index) ||
      path === currentPath;
  }

  const prioprityNavigationActive = prioprityNavigationActiveIndex === index;

  // top level active links and same level active links
  const topLevelActive =
    (topLevelActiveLink && index === topLevelActiveLink?.index && !subNavActive) ||
    (sameLevelActiveLink &&
      index === sameLevelActiveLink?.index &&
      !subNavActive &&
      !prioprityNavigationActive);
  const activeTopLevel =
    secondLevelActiveLink?.href === currentPath &&
    secondLevelActiveLink?.index === index &&
    subNavActive;

  const overviewActive =
    sameLevelActiveLink?.href === currentPath &&
    index === -1 &&
    subNavActive?.index === sameLevelActiveLink?.index;

  return (
    <UeContainer propertyPath="sameLevelInpageNavigation" label="Navigation item">
      <UeReference
        propertyPath={isSecondaryLevel ? `secondlevel.${index}` : `toplevel.${index}`}
        label="Link field"
      >
        <StyledLink
          aria-label={ariaLabel}
          className={`sibling-link ${prioprityNavigationActive ? 'active-link' : isSecondaryLevel ? `secondary-level-link ${secondLevelActive || overviewActive ? 'active-link' : ''}` : `top-level-link ${topLevelActive || activeTopLevel ? 'active-link' : ''}`}`}
          data-testid="navigation-item"
          href={path}
          id={`text-link-${index}`}
          onClick={(e) => {
            e.preventDefault();

            // Get the link element that was clicked. If the target is not an a-tag, get the closest a-tag.
            const linkElement =
              e.target instanceof HTMLAnchorElement ? e.target : (e.target as Element).closest('a');
            // Get the class list of the link element.
            const classList = linkElement?.classList;
            // Check if the link is currently active.
            const isActive = classList?.contains('active-link');
            const list =
              typeof window !== 'undefined'
                ? document.querySelectorAll('a.semi-active-link')
                : undefined;

            if (list && classList && !isSecondaryLevel) {
              list.forEach((item) => item.classList.remove('semi-active-link'));
            }
            classList?.add('semi-active-link');
            const href = linkElement?.getAttribute('href') || path;

            // tracking for link clicks
            if (!hasSubMenu && !isActive) {
              const isParentPresent = parentPageInpageNavigation !== undefined;
              const trackingTitle = isSecondaryLevel
                ? `${topLevelActiveLink?.text} > ${title}`
                : title;
              const trackingPosition: string = isSecondaryLevel
                ? `${getIndexPosition(topLevelActiveLink?.index as number, isParentPresent)}-${getIndexPosition(index)}`
                : getIndexPosition(index, isParentPresent).toString();

              const relatedElements = isSecondaryLevel
                ? subLinksRelatedSingleElement(
                    index,
                    hasSubLinks(topLevelActiveLink?.index as number, sameLevelInpageNavigation),
                  )
                : topLevelRelatedSingleElement(index, sameLevelInpageNavigation);

              trackClickEvent(
                tracking,
                trackingTitle,
                impressionTracking?.value || '',
                path,
                trackingPosition,
                relatedElements,
              );
            }

            // If the link is not active, or submenu toggle call the handleSelectedItem function.
            if (handleSelectedItem && (!isActive || hasSubMenu) && !topLevelActive) {
              handleSelectedItem({
                id,
                index,
                text: title,
                subLevelChildLabel,
                href: path,
                target,
              });
            }

            // If the link is not active, trigger default link behavior.
            if (typeof window !== 'undefined' && !hasSubMenu && path && !isActive) {
              // 200 milisecond delay to allow for tracking click event
              setTimeout(() => {
                // eslint-disable-next-line no-unused-expressions
                target === '_blank' ? window.open(href) : (window.location.href = href);
              }, 200);
            }
          }}
          rel="noopener"
          target={target}
          title={title}
          data-index={index}
          $hasParentElement={hasParentElement}
          $isSecondaryLevel={isSecondaryLevel}
          $isLastElement={isLastElement}
        >
          <UeElement type="text" property="Text" label="Navigation item">
            {title}
          </UeElement>
        </StyledLink>
      </UeReference>
    </UeContainer>
  );
};
